import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Zap, TrendingUp, DollarSign, Users, RefreshCw,
  Settings, Plus, ChevronRight, Activity, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SupplyBar({ sold, available, total }) {
  const soldPct = total > 0 ? (sold / total) * 100 : 0;
  const availPct = total > 0 ? (available / total) * 100 : 0;
  return (
    <div className="space-y-2">
      <div className="h-4 w-full bg-[hsl(220,16%,12%)] rounded-full overflow-hidden flex">
        <div className="h-full bg-[#E8520F] transition-all duration-700" style={{ width: `${soldPct}%` }} title="Sold" />
        <div className="h-full bg-[#10B981] transition-all duration-700" style={{ width: `${availPct - soldPct}%` }} title="Available in LP" />
      </div>
      <div className="flex items-center gap-4 text-[10px] text-[hsl(215,16%,56%)]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#E8520F] inline-block" /> Sold: {sold?.toLocaleString()}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#10B981] inline-block" /> LP Available: {available?.toLocaleString()}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[hsl(220,16%,18%)] inline-block" /> Reserve: {(total - available)?.toLocaleString()}</span>
      </div>
    </div>
  );
}

export default function AdminGlabs() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [priceInput, setPriceInput] = useState('');
  const [mintUserId, setMintUserId] = useState('');
  const [mintAmount, setMintAmount] = useState('');
  const [mintReason, setMintReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [minting, setMinting] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/glabs/stats`, { withCredentials: true });
      setStats(data);
      setPriceInput(data.price_per_credit.toFixed(4));
    } catch (err) {
      toast.error('Failed to load GLabs stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSetPrice = async () => {
    const p = parseFloat(priceInput);
    if (!p || p < 0.01) { toast.error('Minimum price is $0.01'); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/admin/glabs/price`,
        { price_per_credit_usdt: p }, { withCredentials: true });
      toast.success(data.message);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update price');
    }
    setSaving(false);
  };

  const handleMint = async () => {
    if (!mintUserId || !mintAmount) { toast.error('Enter user ID and amount'); return; }
    const amt = parseInt(mintAmount);
    if (amt < 1) { toast.error('Minimum 1 credit'); return; }
    setMinting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/glabs/mint`,
        { user_id: mintUserId, credits_amount: amt, reason: mintReason },
        { withCredentials: true });
      toast.success(data.message);
      setMintUserId(''); setMintAmount(''); setMintReason('');
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mint failed');
    }
    setMinting(false);
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[#D4A017] animate-spin" />
      </div>
    );
  }

  const { supply, revenue_usdt, price_per_credit, top_buyers, recent_transactions, daily_chart } = stats;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-white flex items-center gap-2">
            <Zap className="w-7 h-7 text-[#D4A017]" /> GLabs Credit Tracker
          </h1>
          <p className="text-[hsl(215,16%,56%)]">Supply, sales, revenue, and credit price management</p>
        </div>
        <Button onClick={fetchStats} variant="outline" size="sm" className="border-[hsl(220,16%,18%)] text-white bg-transparent">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Supply Overview */}
      <Card className="border-[hsl(220,16%,18%)] shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#D4A017]" /> Token Supply
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <SupplyBar sold={supply.sold} available={supply.lp_available} total={supply.total} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Total Supply', value: (supply.total / 1e6).toFixed(0) + 'M', color: 'text-white' },
              { label: 'LP Available', value: (supply.lp_available / 1e6).toFixed(0) + 'M', color: 'text-[#10B981]' },
              { label: 'Sold', value: supply.sold.toLocaleString(), color: 'text-[#E8520F]' },
              { label: 'Sold %', value: supply.sold_pct + '%', color: 'text-[#D4A017]' },
            ].map(({ label, value, color }) => (
              <div key={label} className="p-3 rounded-lg bg-[hsl(220,16%,14%)] border border-[hsl(220,16%,18%)]">
                <p className="text-[10px] text-[hsl(215,16%,45%)]">{label}</p>
                <p className={`text-xl font-bold font-mono mt-1 ${color}`}>{value}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Revenue + KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-[hsl(220,16%,18%)] shadow-none">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-[#10B981]" />
              <p className="text-sm text-[hsl(215,16%,56%)]">Total Revenue</p>
            </div>
            <p className="text-3xl font-bold text-[#10B981] font-mono">${revenue_usdt.toFixed(2)}</p>
            <p className="text-xs text-[hsl(215,16%,45%)] mt-1">USDT collected</p>
          </CardContent>
        </Card>
        <Card className="border-[hsl(220,16%,18%)] shadow-none">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-[#D4A017]" />
              <p className="text-sm text-[hsl(215,16%,56%)]">Current Price</p>
            </div>
            <p className="text-3xl font-bold text-[#D4A017] font-mono">${price_per_credit.toFixed(4)}</p>
            <p className="text-xs text-[hsl(215,16%,45%)] mt-1">per credit (USDT)</p>
          </CardContent>
        </Card>
        <Card className="border-[hsl(220,16%,18%)] shadow-none">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-[#6366F1]" />
              <p className="text-sm text-[hsl(215,16%,56%)]">Top Buyers</p>
            </div>
            <p className="text-3xl font-bold text-white font-mono">{top_buyers.length}</p>
            <p className="text-xs text-[hsl(215,16%,45%)] mt-1">unique accounts</p>
          </CardContent>
        </Card>
      </div>

      {/* Daily Sales Chart (simple bar) */}
      {daily_chart.length > 0 && (
        <Card className="border-[hsl(220,16%,18%)] shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#E8520F]" /> Daily Sales (last 7 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-24">
              {daily_chart.map(d => {
                const maxVal = Math.max(...daily_chart.map(x => x.credits), 1);
                const pct = (d.credits / maxVal) * 100;
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-[10px] text-[hsl(215,16%,56%)] font-mono">{d.credits}</span>
                    <div className="w-full rounded-t bg-[#D4A017]/80 transition-all" style={{ height: `${pct}%`, minHeight: 4 }} />
                    <span className="text-[9px] text-[hsl(215,16%,45%)]">{d.date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Controls: Price + Mint */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Price Control */}
        <Card className="border-[hsl(220,16%,18%)] shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#D4A017]" /> Price Control
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 rounded-lg bg-[hsl(220,16%,14%)] border border-[hsl(220,16%,18%)]">
              <p className="text-xs text-[hsl(215,16%,56%)]">Current: <span className="font-mono text-white">${price_per_credit.toFixed(4)}</span> per credit</p>
              <p className="text-[10px] text-[hsl(215,16%,45%)] mt-1">Minimum floor: $0.01 · Free-form pricing</p>
            </div>
            <div className="space-y-2">
              <Label className="text-white text-xs">New Price (USDT per credit)</Label>
              <Input value={priceInput} onChange={e => setPriceInput(e.target.value)}
                placeholder="0.25" type="number" step="0.0001" min="0.01"
                className="border-[hsl(220,16%,18%)] font-mono" />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[0.10, 0.25, 0.50, 1.00].map(p => (
                <button key={p} type="button" onClick={() => setPriceInput(p.toFixed(2))}
                  className="px-2 py-1 text-[10px] font-mono rounded border border-[hsl(220,16%,18%)] text-[hsl(215,16%,56%)] hover:text-white hover:border-[#D4A017] transition-colors">
                  ${p.toFixed(2)}
                </button>
              ))}
            </div>
            <Button onClick={handleSetPrice} disabled={saving}
              className="w-full bg-[#D4A017] hover:bg-[#B8860B] text-white">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
              Update Price
            </Button>
          </CardContent>
        </Card>

        {/* Manual Mint */}
        <Card className="border-[hsl(220,16%,18%)] shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#10B981]" /> Manual Mint
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <p className="text-xs text-amber-400">Issue free credits to any user. Recorded in audit log.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-white text-xs">User ID (MongoDB ObjectId)</Label>
              <Input value={mintUserId} onChange={e => setMintUserId(e.target.value)}
                placeholder="6456a..." className="border-[hsl(220,16%,18%)] font-mono text-xs" />
            </div>
            <div className="space-y-2">
              <Label className="text-white text-xs">Credits to Mint</Label>
              <Input value={mintAmount} onChange={e => setMintAmount(e.target.value)}
                placeholder="100" type="number" min="1" className="border-[hsl(220,16%,18%)] font-mono" />
            </div>
            <div className="space-y-2">
              <Label className="text-white text-xs">Reason (optional)</Label>
              <Input value={mintReason} onChange={e => setMintReason(e.target.value)}
                placeholder="Refund / Promotion / Bonus" className="border-[hsl(220,16%,18%)]" />
            </div>
            <Button onClick={handleMint} disabled={minting}
              className="w-full bg-[#10B981] hover:bg-[#059669] text-white">
              {minting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Mint Credits
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Top Buyers */}
      {top_buyers.length > 0 && (
        <Card className="border-[hsl(220,16%,18%)] shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#6366F1]" /> Top Buyers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {top_buyers.map((b, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-[hsl(220,16%,18%)] hover:bg-[hsl(220,16%,14%)] transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#D4A017]/10 text-[#D4A017] text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <div>
                      <p className="text-sm text-white">{b.email || b.user_id.slice(0, 12) + '...'}</p>
                      <p className="text-[10px] text-[hsl(215,16%,45%)] font-mono">{b.user_id.slice(0, 16)}...</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-[#D4A017] font-mono">{b.total_credits} credits</p>
                    <p className="text-[10px] text-[hsl(215,16%,45%)]">${b.total_usdt.toFixed(2)} spent</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Transactions */}
      {recent_transactions.length > 0 && (
        <Card className="border-[hsl(220,16%,18%)] shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#00CED1]" /> Recent Sales
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {recent_transactions.slice(0, 20).map((tx, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border border-[hsl(220,16%,18%)]">
                  <div>
                    <p className="text-xs text-white">{tx.user_email || tx.user_id?.slice(0, 12)}</p>
                    <p className="text-[10px] text-[hsl(215,16%,45%)]">
                      {tx.currency} · {new Date(tx.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-[#D4A017] font-mono">+{tx.credits_added}c</p>
                    <p className="text-[10px] text-[hsl(215,16%,45%)]">${(tx.usdt_value || 0).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
